// src/Component/api/apiFetch.ts
//
// Central fetch wrapper used by all backend calls.
//
//   - Attaches `Authorization: Bearer <token>` from the tokenStore.
//   - On 401, calls the refresh-token endpoint once (concurrent 401s share
//     a single in-flight refresh), then retries the original request.
//   - If refresh fails, clears tokens and sends the user to /signin.
//   - Preserves FormData uploads — never overrides the multipart Content-Type
//     boundary set by the browser.
//
// Callers that must NOT carry the token (SIGNIN, VERIFY_OTP) pass
// `{ skipAuth: true }`.

import { API_URLS } from "./url";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from "./tokenStore";

export interface ApiFetchOptions extends RequestInit {
  skipAuth?: boolean;
}

// Shared in-flight refresh — guarantees a single refresh call when multiple
// requests 401 at the same time.
let refreshInFlight: Promise<string | null> | null = null;

const performRefresh = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const response = await fetch(API_URLS.REFRESH_TOKEN, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh: refreshToken }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (data.success !== "1") return null;
    const newAccess: string | null = data.access ?? null;
    if (!newAccess) return null;
    // Backend rotates only the access token; refresh token stays valid until
    // it expires on its own. Passing `undefined` keeps the existing one.
    setTokens(newAccess);
    return newAccess;
  } catch {
    return null;
  }
};

const refreshAccessToken = (): Promise<string | null> => {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

const handleAuthFailure = (): void => {
  clearTokens();
  // Also drop the cached user object. Without this, AuthContext restores the
  // user from localStorage on the next page load and `isAuthenticated` stays
  // true — which sends /signin straight back to /dashboard, which 401s again,
  // producing an infinite signin↔dashboard reload loop. Keys must match
  // USER_CACHE_KEY / USER_CACHE_VERSION_KEY in AuthContext.
  try {
    localStorage.removeItem("user");
    localStorage.removeItem("user_cache_version");
  } catch {
    // localStorage unavailable — nothing to clear.
  }
  // Notify the React layer that the session is dead. AuthContext listens for
  // this event, clears its in-memory user state, and lets ProtectedRoute do
  // a client-side <Navigate> to /signin — no full page reload. This is what
  // makes the redirect feel instant instead of a jarring browser refresh.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("auth:session-expired"));
  }
};

const buildHeaders = (init: ApiFetchOptions, token: string | null): Headers => {
  const headers = new Headers(init.headers || {});
  const isFormData = init.body instanceof FormData;
  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!init.skipAuth && token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
};

export const apiFetch = async (
  url: string,
  init: ApiFetchOptions = {}
): Promise<Response> => {
  const { skipAuth, ...rest } = init;

  const token = skipAuth ? null : getAccessToken();
  const response = await fetch(url, {
    ...rest,
    headers: buildHeaders(init, token),
  });

  if (response.status !== 401 || skipAuth) {
    return response;
  }

  const newToken = await refreshAccessToken();
  if (!newToken) {
    handleAuthFailure();
    return response;
  }

  return fetch(url, {
    ...rest,
    headers: buildHeaders(init, newToken),
  });
};
